import React, { useContext, useEffect } from 'react';
import styles from './privacy.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData: user } = useContext(AuthContext);

  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, location, navigate]);

  return (
    <div className={styles.privacyContainer}>
      <h1 className={styles.mainHeading}>Mai Cash Privacy Policy</h1>

      <div className={styles.contentWrapper}>
        <p className={styles.introParagraph}>
          Mai Cash (the "Platform") is made available by Mai Labs (the
          "Company", "we", "our", or "us"). We are committed to protecting your
          privacy. This Privacy Policy explains how personal data is collected,
          used, shared, and safeguarded when the Platform is made available to
          you by the Company as part of its closed-loop employee benefit and
          redemption programme. This Privacy Policy applies to our website,
          APIs, and mobile applications, and any related services and subdomains
          (collectively, the "Services"). It is intended to be read together
          with the Mai Cash Terms and Conditions. We process personal data in
          accordance with applicable Indian data protection laws, including the
          Digital Personal Data Protection Act, 2023 and applicable rules
          thereunder (as amended from time to time). By using our Services, you
          confirm that you have read and understood this Privacy Policy and
          agree to the data handling practices described in this Policy.
        </p>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>1. Definitions</h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.1. "Platform" means the Mai Cash closed loop digital cash
              management system described in the Terms and Conditions.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.2. "Company" means Kalp Digital Infra Private Limited (Kalp
              Studio), which operates and maintains the Platform as a service
              provider to the Organization.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.3. "Organization" means the corporate client that has subscribed
              to the Platform and is responsible for allocating Tokens to
              Employees and approving Vendor cash out requests.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.4. "Employee" means an individual in an employment or similar
              relationship with the Organization who is authorized to use the
              Platform and receive or spend Tokens.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.5. "Vendor" means a third-party service provider, contractor or
              business partner approved by the Organization to accept Tokens as
              internal payment and, where enabled, to request settlement of
              Tokens to its bank account through a Cash-Out Request, subject to
              Organization approval.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.6. "Token" "Token" means a stable-value digital unit recorded on
              the Platform and made available by the Organization for internal
              corporate financial operations within the closed-loop ecosystem
              enabled by Mai Cash. Tokens are pegged to real currency for
              value-stability within the Platform, but Tokens are not legal
              tender, are not a bank deposit, and have no use or acceptance
              outside the permitted transactions and Users authorized by the
              Organization on the Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.7. , "Gift Card" means a prepaid gift voucher, gift card, or
              voucher code that may be made available through the Platform for
              redemption against the goods/services of a third-party
              merchant/brand ("Merchant"), and that is fulfilled through the
              Gift Card Partner and issued by the Merchant (not by the Company).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.8. "Gift Card Partner" means Gullak Technologies Private Limited
              ("Gullak"), acting as the Organization's gift card
              reseller/fulfilment partner for facilitating Gift Card fulfilment
              through the Platform, and includes any replacement or additional
              fulfilment partner appointed by the Organization from time to
              time.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.9. "Merchant" means the third-party brand/issuer whose Gift Card
              is issued and redeemed, and who determines the applicable Gift
              Card terms (including redemption rules, expiry/validity, and
              participating outlets), which may change from time to time.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            2. Scope of this Privacy Policy
          </h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.1. This Privacy Policy applies to personal data collected and
              processed through the Platform and any associated mobile or web
              interfaces used by the Company's authorized employees to access
              and use the Platform, including to view Token balances and
              transaction history, to make payments to registered Vendors via QR
              workflows (where enabled), and to purchase Gift Cards through the
              Company's Gift Card Partner (where enabled). This Privacy Policy
              also applies to personal data processed when the Company onboards
              or offboards employees, when employees authenticate using one-time
              passwords ("OTPs") delivered to registered email addresses, and
              when employees contact Platform support.
            </p>
            <p className={styles.paragraph}>
              The Company may process Vendor personal data as part of enabling
              Vendor registration, processing Vendor cash-out requests (where
              enabled), and facilitating off-Platform settlement by the Company.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.2. This Privacy Policy does not apply to any websites,
              applications, or services that are not operated by the Company,
              even if accessible via links in the Platform. In particular, Gift
              Cards are issued and governed by third-party merchants/brands and
              may be fulfilled via third-party partners (including the Company's
              Gift Card Partner). Any personal data you provide to, or that is
              collected by, such third parties is governed by their own privacy
              notices and terms, and you should review those policies before
              using their services.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.3. This Privacy Policy forms part of the documentation governing
              your use of the Platform alongside the Mai Cash Terms and
              Conditions. In the event of any inconsistency, the Terms govern
              service use and contractual rights and obligations, and this
              Privacy Policy governs how personal data is collected and
              processed, in each case subject to Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.4. The Platform is intended for use by authorized employees of
              the Company and is not designed for use by children. You must be
              at least 18 years of age to use the Platform, unless the Company
              expressly authorizes a minor in exceptional circumstances and
              assumes responsibility for such access and supervision. If you
              believe a minor has been onboarded without appropriate
              authorization, please notify the Company at (*)
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            3. Identity of the Data Fiduciary
          </h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>3.1. Data Fiduciary:</strong> For the purposes of the
              Digital Personal Data Protection Act, 2023 and applicable rules
              thereunder, Mai Labs is the Data Fiduciary in respect of personal
              data processed through the Platform, as the Company determines the
              purposes and means of such processing in connection with operation
              of the Platform, user access enablement, Token administration, and
              Vendor settlement workflows (where enabled).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>3.2. Data Processors and Independent Processing:</strong>{' '}
              The Company may engage third-party service providers to support
              operation of the Platform, including technology, infrastructure,
              hosting, security, analytics, and support services. Such service
              providers act as data processors and process personal data only on
              the Company's documented instructions, in accordance with
              applicable law and contractual arrangements with the Company.
            </p>
            <p className={styles.paragraph}>
              The Company may also process personal data for its own purposes
              where necessary to comply with Applicable Law, to secure and
              protect the Platform, to maintain audit and security logs, to
              prevent fraud and misuse, and to manage service communications,
              updates, and operational notices relating to the Platform.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>4. Personal Data We Collect</h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>4.1. Information you provide directly:</strong>
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                Account and profile data (Employees): name, Company email
                address, employee identifier, job title/role, and other
                information necessary for onboarding and account management.
              </li>
              <li className={styles.listItem}>
                Vendor and settlement data (Vendors, where enabled): Vendor
                name, contact details, tax/invoicing information (if collected),
                and payout particulars such as bank account details and
                settlement identifiers, provided for processing cash-out
                requests and off-Platform settlement by the Company.
              </li>
              <li className={styles.listItem}>
                Communications: information you provide when contacting support,
                reporting issues, raising requests relating to Gift Cards, or
                raising a Vendor cash-out request (where enabled).
              </li>
            </ol>
            <p className={styles.paragraph}>
              The Platform is not intended for public posting, messaging, or
              user-generated content hosting. Please do not input or store any
              unlawful information, sensitive personal data not required for
              Platform workflows, or data that infringes third-party rights.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>4.2. Information collected automatically:</strong>
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                Transaction data: Token balances, allocations, payments to
                Vendors (where enabled), Gift Card purchase details and
                fulfilment status, cash-out request status and approvals (where
                enabled), and related transaction references.
              </li>
              <li className={styles.listItem}>
                Technical data: IP address, device identifiers, browser type,
                operating system, timestamps of Platform access and
                security/audit events.
              </li>
              <li className={styles.listItem}>
                Usage data: login frequency, features used, performance and
                error logs, and diagnostic data used to improve service quality
                and prevent fraud.
              </li>
            </ol>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>4.3. Information from third parties:</strong>
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                The Company may receive information from internal Company
                systems or authorised personnel to manage user access (for
                example, employment/role status needed for joiner–mover–leaver
                controls).
              </li>
              <li className={styles.listItem}>
                Gift Card Partners and Merchants may share fulfilment status,
                redemption information, and fraud/risk alerts with the Company
                for transaction processing, support, and fraud prevention.
              </li>
            </ol>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            5. How We Use Your Personal Data
          </h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>5.1. Service delivery and account management:</strong>
            </p>
            <p className={styles.paragraph}>
              To create and manage your Platform access, authenticate you via
              OTP, maintain Token balances and transaction history, process
              Employee payments to registered Vendors (where enabled), enable
              Gift Card purchases via the Gift Card Partner (where enabled), and
              administer Vendor cash-out requests and related off-Platform
              settlement workflows (where enabled).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>5.2. Compliance and security:</strong>
            </p>
            <p className={styles.paragraph}>
              To operate the Platform securely, detect and prevent fraud and
              misuse, maintain audit and security logs, enforce our Terms,
              comply with Applicable Law (including responding to lawful
              requests by competent authorities), and protect the rights,
              safety, and integrity of the Company, Users, Vendors, and the
              Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>5.3. Communications and support:</strong>
            </p>
            <p className={styles.paragraph}>
              To send service and transaction communications (for example, OTPs,
              payment confirmations, Gift Card codes/links, status updates, and
              support responses), respond to your enquiries, and provide alerts
              about policy updates or service changes. We will not send you
              marketing communications unless you have separately opted in where
              required.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>5.4. Analytics and improvement:</strong>
            </p>
            <p className={styles.paragraph}>
              To analyse usage and performance trends to improve the Platform,
              reliability, and security. Where feasible, we use aggregated or
              de-identified information for analytics and reporting.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            6. Legal Basis for Processing
          </h2>
          <p className={styles.paragraph}>
            The Company processes personal data in accordance with the Digital
            Personal Data Protection Act, 2023 and applicable rules thereunder.
            Depending on the context, processing may be carried out based on:
          </p>
          <ol className={styles.orderedList}>
            <li className={styles.listItem}>
              your consent, including consent obtained through your use of the
              Platform and/or Company onboarding/offboarding processes; and/or
            </li>
            <li className={styles.listItem}>
              permitted grounds under Applicable Law, including processing
              necessary for providing the Services you request through the
              Platform, maintaining security, preventing fraud and misuse,
              record-keeping and audit, and compliance with legal obligations
              and lawful requests.
            </li>
          </ol>
          <p className={styles.paragraph}>
            The Company may engage third-party service providers as data
            processors to support the Platform, who process personal data only
            on the Company's documented instructions and in accordance with
            applicable law and contractual safeguards.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>7. Sharing and Disclosure</h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>7.1. Within the Company:</strong>
            </p>
            <p className={styles.paragraph}>
              Authorized personnel of the Company may access personal data on a
              need-to-know basis to administer the Platform, including
              onboarding/offboarding, Token administration, processing Employee
              payments to registered Vendors (where enabled), processing Vendor
              cash-out requests and off-Platform settlement (where enabled),
              providing support, resolving disputes, and meeting Applicable Law
              obligations.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>7.2. Gift Card Partner and Merchants:</strong>
            </p>
            <p className={styles.paragraph}>
              When you purchase a Gift Card (where enabled), the Company shares
              the minimum personal data and transaction information necessary
              with the relevant Gift Card fulfilment partner and/or the relevant
              merchant/brand issuer ("Merchant") to facilitate fulfilment,
              issue/deliver the Gift Card, record and reconcile the transaction,
              and provide customer support (for example, name, email address,
              delivery address (if applicable), Token amount/value, and
              transaction identifiers). Such parties may process the data for
              fulfilment, support, fraud prevention, compliance with Applicable
              Law, and responding to lawful requests by competent authorities,
              in accordance with their own privacy notices and legal
              obligations. We require our partners, where contractually
              applicable, to protect personal data and to process it only for
              the purposes described above or as required by law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>7.3. Service providers:</strong>
            </p>
            <p className={styles.paragraph}>
              We engage third-party service providers (for example, cloud
              hosting providers, OTP/email delivery providers, customer support
              tools, and security service providers) to operate and support the
              Platform. These service providers act as data processors, are
              bound by contractual obligations to protect personal data, and may
              process personal data only on our documented instructions.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>7.4. Regulators and legal requests:</strong>
            </p>
            <p className={styles.paragraph}>
              We may disclose personal data where required by Applicable Law,
              court order, or lawful request by a competent authority, or where
              necessary to protect rights, safety, or property, investigate
              fraud or security incidents, or enforce our Terms.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>7.5. Transfers outside India:</strong>
            </p>
            <p className={styles.paragraph}>
              The Platform is designed primarily for use in India. Where
              cross-border transfer of personal data is necessary (for example,
              use of service providers with infrastructure outside India), we
              will take reasonable steps to ensure such transfers comply with
              Applicable Law.
            </p>
          </div>

          <p className={styles.paragraph}>
            We do not sell or rent personal data. Employees cannot transfer
            Tokens or personal data to other Employees; permitted Token
            workflows are defined in the Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            8. Cookies and Similar Technologies
          </h2>
          <p className={styles.paragraph}>
            The Platform may use session cookies, local storage, and similar
            technologies that are necessary to maintain login sessions, support
            OTP authentication flows, preserve user preferences, and protect the
            Platform against fraud and unauthorised access. The Platform is not
            designed to use cookies for cross-site behavioural advertising or to
            track Users across third-party websites. Where the Platform
            integrates services operated by third parties (such as Gift Card
            fulfilment by a Gift Card Partner or redemption by a Merchant),
            those third parties may apply their own cookies or similar
            technologies under their own policies, and the Company does not
            control such practices. You can control cookies through your browser
            or device settings; however, disabling strictly necessary cookies or
            local storage may prevent you from logging in or using certain
            Platform features reliably.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>9. Data Retention</h2>
          <p className={styles.paragraph}>
            We retain personal data only for as long as necessary for the
            purposes set out in this Privacy Policy and as required by
            Applicable Law, including accounting, audit, tax, security, dispute
            management, and enforcement of our Terms. Transaction and accounting
            records relating to Token issuance and allocation, payments to
            Vendors (where enabled), Gift Card purchases, and Vendor cash-out
            requests/approvals (where enabled) are retained for the period
            required under Applicable Law and internal retention requirements.
            Security and operational logs are maintained for a reasonable period
            and may be retained longer where necessary for incident
            investigation, audit, dispute resolution, or compliance with lawful
            directions. Account and profile data is retained while you remain an
            authorised employee User and for a reasonable period thereafter to
            support offboarding, reversals, and dispute resolution. At the end
            of the applicable retention period, personal data is securely
            deleted, anonymised, or archived with access restrictions, subject
            to lawful holds. After your authorization ends, you may lose access
            to your Wallet and transaction history, but the Company may retain
            records as required for legitimate business purposes and legal
            compliance.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>10. Security Measures</h2>
          <p className={styles.paragraph}>
            We implement appropriate technical and organisational security
            measures designed to protect personal data against accidental or
            unlawful destruction, loss, alteration, unauthorised disclosure, or
            unauthorised access, consistent with Applicable Law. These measures
            may include encryption of sensitive data in transit and, where
            appropriate, at rest; authentication using one-time passwords
            ("OTPs") delivered to registered email addresses; role-based access
            controls and least-privilege administration; logging and monitoring;
            secure development and change-management practices; and periodic
            security testing. We require our service providers and processors to
            implement reasonable security controls and to process personal data
            only on our documented instructions. In the event of a suspected or
            confirmed personal data breach or cyber security incident affecting
            the Platform, we will take reasonable steps to contain, assess, and
            remediate the incident and will comply with applicable reporting and
            notification obligations under Applicable Law. Users must also
            protect their access credentials and must promptly notify the
            Company at (*) of any suspected compromise.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>11. Your Rights</h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.1. Access and information:</strong>
            </p>
            <p className={styles.paragraph}>
              Subject to Applicable Law, you may request from the Company a
              summary of the personal data processed about you through the
              Platform and information regarding such processing, including the
              identities of data processors with whom such personal data has
              been shared (where required to be provided under Applicable Law).
              This right is subject to lawful exclusions and limitations,
              including restrictions under the Digital Personal Data Protection
              Act, 2023.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.2. Correction:</strong>
            </p>
            <p className={styles.paragraph}>
              You may request correction, completion, or updating of your
              personal data where it is inaccurate, misleading, incomplete, or
              out of date, in accordance with Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.3. Erasure:</strong>
            </p>
            <p className={styles.paragraph}>
              You may request erasure of personal data for which processing is
              based on consent. Upon receiving a valid request, the Company will
              erase such personal data unless retention is necessary for the
              purposes set out in this Privacy Policy or for compliance with
              Applicable Law (including record-keeping, audit, dispute
              resolution, security, and legal compliance requirements).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.4. Withdrawal of consent:</strong>
            </p>
            <p className={styles.paragraph}>
              Where processing is based on your consent, you may withdraw your
              consent through Platform features made available to you or through
              other means described in the notice provided to you. The
              withdrawal process will be no more burdensome than the process by
              which consent was provided. Withdrawal does not affect the
              lawfulness of processing carried out prior to withdrawal and may
              result in suspension or termination of your access to the Platform
              or to specific features.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.5. Nomination:</strong>
            </p>
            <p className={styles.paragraph}>
              You may, in accordance with Applicable Law and the terms of access
              arranged by the Company, nominate one or more individuals who may,
              in the event of your death or incapacity, exercise your rights
              under Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>11.6. Grievance redressal and escalation:</strong>
            </p>
            <p className={styles.paragraph}>
              You may submit a grievance relating to the processing of your
              personal data or the exercise of your rights by contacting the
              Company at (*).
            </p>
            <p className={styles.paragraph}>
              To enable us to act on your request, we may verify your identity
              using reasonable means (for example, confirming control over your
              registered email address and account identifiers). We will respond
              to grievances within the timeframe required under Applicable Law
              and our grievance redressal mechanism. Under the Digital Personal
              Data Protection Act, 2023, you may be required to exhaust the
              Company's grievance redressal process before approaching the Data
              Protection Board of India.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            12. Contact, Grievances and Data Protection Officer
          </h2>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>12.1. Support Contact:</strong>
            </p>
            <p className={styles.paragraph}>
              For Platform support (including access issues, OTP issues, and
              Gift Card-related queries), you may contact the Company at (*).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>12.2. Data Protection Officer (Company):</strong>
            </p>
            <p className={styles.paragraph}>
              For matters relating to this Privacy Policy, your personal data,
              or to exercise your rights under Applicable Law, you may contact
              the Company's designated Privacy/Grievance Officer: (*)
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              <strong>12.3. Privacy Queries, Requests and Complaints:</strong>
            </p>
            <p className={styles.paragraph}>
              Privacy-related queries, requests (including
              access/correction/erasure), and complaints may be submitted using
              the contact details above. We will acknowledge and respond in
              accordance with Applicable Law. For more information on your
              rights and the grievance redressal process, see Clause 11 (Your
              Rights).
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>13. Changes to this Policy</h2>
          <p className={styles.paragraph}>
            We may amend this Privacy Policy from time to time to reflect
            changes in Applicable Law, technology, or our Services. If any
            change materially affects your rights, we will provide reasonable
            notice through the Platform and/or via email to your registered
            address, unless a shorter notice period is required for legal,
            security, or operational reasons. The updated Privacy Policy will be
            effective from the "Effective Date" stated in the revised policy.
            Continued use of the Platform after the effective date constitutes
            acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>
            14. Governing Law and Dispute Resolution
          </h2>
          <p className={styles.paragraph}>
            This Privacy Policy is governed by the laws of India. Any dispute
            arising out of or relating to this Privacy Policy shall be resolved
            in accordance with the governing law and dispute resolution
            provisions set out in the Mai Cash Terms and Conditions.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
